import React from 'react'
import { FieldArrayPath, useFieldArray, useFormContext } from 'react-hook-form'
import { FaPlus } from 'react-icons/fa'
import styled from 'styled-components'
import Entry from './Entry'
import { EMPTY_BLUEPRINT_ENTRY } from '../AccountingRuleForm'
import { IAccountingRule } from '@root/query/accountingRules'

interface Props {
  baseName: FieldArrayPath<IAccountingRule>
  header: string
  buttonLabel: string
}

const EntriesSelector: React.FC<Props> = ({ baseName, header, buttonLabel }) => {
  const { control } = useFormContext<IAccountingRule>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: baseName
  })

  return (
    <Card>
      <CardHeader>{header}</CardHeader>
      {fields.map((field, index) => (
        <EntryRow key={field.id} $first={index === 0}>
          <Entry
            baseName={baseName}
            index={index}
            control={control}
            canRemove={fields.length > 1}
            onRemove={() => remove(index)}
          />
        </EntryRow>
      ))}
      <AddRowButton type="button" onClick={() => append(EMPTY_BLUEPRINT_ENTRY)}>
        <FaPlus />
        {buttonLabel}
      </AddRowButton>
    </Card>
  )
}

export default EntriesSelector

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
`

const CardHeader = styled.div`
  background: ${({ theme }) => theme.dashboard.surfaceSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const EntryRow = styled.div<{ $first: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-top: ${({ theme, $first }) => ($first ? 'none' : `1px solid ${theme.colors.borderSoft}`)};
`

const AddRowButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem
    ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.dashboard.infoDeep};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  cursor: pointer;

  svg {
    font-size: ${({ theme }) => theme.fontSize.px10}rem;
  }

  &:hover {
    background: ${({ theme }) => theme.dashboard.infoTint};
  }
`
